import React, { createContext, useContext, useEffect, useState } from 'react';
import { useWallet } from './WalletContext';
import ContractServiceFactory from '../services/contract/ContractServiceFactory';
import { getNetworkName } from '../constants/contracts';
import { ethers } from 'ethers';

const ContractContext = createContext(null);

/**
 * Provider component for contract services
 * @param {Object} props - Component props
 * @returns {JSX.Element} - Provider component
 */
export const ContractProvider = ({ children }) => {
  const { provider, account, chainId, signer, isConnected, networkName } =
    useWallet();
  const [serviceFactory, setServiceFactory] = useState(null);
  const [services, setServices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create Infura read-only provider
  const infuraProvider = new ethers.providers.JsonRpcProvider(
    `${process.env.REACT_APP_INFURA_API_URL}/${process.env.REACT_APP_INFURA_API_KEY}`
  );

  // Fallback provider
  const effectiveProvider = provider || infuraProvider;

  // Initialize contract services when provider is available
  useEffect(() => {
    setLoading(true);
    setError(null);

    try {
      // Get network ID based on chain ID or use the networkName from WalletContext
      let networkId = 'optimismSepolia'; // Default

      // Format conversion: convert OPTIMISM_SEPOLIA to optimismSepolia
      if (networkName) {
        if (networkName === 'OPTIMISM_SEPOLIA') {
          networkId = 'optimismSepolia';
        } else if (networkName === 'OPTIMISM') {
          networkId = 'optimism';
        } else if (networkName === 'LOCALHOST') {
          networkId = 'localhost';
        } else {
          // Handle any other format
          networkId = networkName
            .toLowerCase()
            .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        }
      }
      // Map chainId to networkId if networkName not available
      else if (chainId) {
        // Optimism Sepolia testnet
        if (chainId === '0xaa37dc' || chainId === 11155420) {
          networkId = 'optimismSepolia';
        }
        // Optimism mainnet
        else if (chainId === '0xa' || chainId === 10) {
          networkId = 'optimism';
        }
        // Localhost
        else if (chainId === 31337) {
          networkId = 'localhost';
        }
        // Add more networks as needed
        else {
          console.warn(
            `Unsupported chainId: ${chainId}. Using default network: ${networkId}`
          );
        }
      }

      console.log('Initializing contract services for network:', networkId);

      // Create service factory

      const effectiveSigner = isConnected && signer ? signer : null;
      const factory = new ContractServiceFactory(
        effectiveProvider,
        networkId,
        effectiveSigner
      );
      //const factory = new ContractServiceFactory(effectiveProvider, networkId, signer);
      setServiceFactory(factory);

      // Get all services
      const allServices = factory.getAllServices();
      setServices(allServices);
      setLoading(false);
    } catch (err) {
      console.error('Error initializing contract services:', err);
      setError(err.message || 'Failed to initialize contract services');
      setLoading(false);
    }
  }, [provider, signer, isConnected, chainId, networkName]);

  // Update services when account or signer changes
  useEffect(() => {
    const updateFactoryWithSigner = () => {
      if (!provider && !signer) return;

      let networkId = 'optimismSepolia';

      if (networkName) {
        if (networkName === 'OPTIMISM_SEPOLIA') networkId = 'optimismSepolia';
        else if (networkName === 'OPTIMISM') networkId = 'optimism';
        else if (networkName === 'LOCALHOST') networkId = 'localhost';
        else
          networkId = networkName
            .toLowerCase()
            .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      } else if (chainId) {
        if (chainId === '0xaa37dc' || chainId === 11155420)
          networkId = 'optimismSepolia';
        else if (chainId === '0xa' || chainId === 10) networkId = 'optimism';
        else if (chainId === 31337) networkId = 'localhost';
      }

      const newFactory = new ContractServiceFactory(
        effectiveProvider,
        networkId,
        signer
      );
      setServiceFactory(newFactory);
      setServices(newFactory.getAllServices());
    };

    updateFactoryWithSigner();
  }, [signer, account, provider, chainId, networkName]);

  const value = {
    services,
    loading,
    error,
    serviceFactory,
  };

  return (
    <ContractContext.Provider value={value}>
      {children}
    </ContractContext.Provider>
  );
};

/**
 * Hook for accessing contract services
 * @returns {Object} - Contract context value
 */
export const useContracts = () => {
  const context = useContext(ContractContext);
  if (context === null) {
    throw new Error('useContracts must be used within a ContractProvider');
  }
  return context;
};

export default ContractContext;
